<template>
  <page-wrapper :title="title" :has-pad="true">
    <loading-overlay :active="isLoading || isSubmitting" />
    <div v-if="form" class="wrapper row">
      <div class="col-md-3 member">
        <p>ชื่อ</p>
        <h6>{{ userPresenter.name }}</h6>
        <p>E-mail</p>
        <h6>{{ userPresenter.email }}</h6>
        <p>เบอร์โทรศัพท์</p>
        <h6>{{ userPresenter.phone_number }}</h6>
        <p>สถานะ</p>
        <h6>{{ userRole }}</h6>
        <p>รูปภาพรายละเอียด</p>
        <div class="image-gallery">
          <div
            v-for="(image, index) in images"
            :key="index"
            class="image-container"
            @click="openImageModal(image, index)"
          >
            <img :src="image" class="img-detail" />
          </div>
        </div>
        <div v-if="powerOfAttorneyPresenter" class="power-of-attorney">
          <p class="section-title">ข้อมูลการมอบอำนาจ</p>
          <p>
            ประเภทบุคคล: <span>{{ grantorTypeLabel }}</span>
          </p>
          <div
            v-for="doc in powerOfAttorneyPresenter.documents"
            :key="doc.field"
            class="poa-document"
          >
            <p>{{ doc.label }}</p>
            <div v-if="doc.url" class="image-gallery">
              <div
                class="image-container"
                @click="openPowerOfAttorneyModal(doc)"
              >
                <img :src="doc.url" class="img-detail" />
              </div>
            </div>
            <h6 v-else>-</h6>
          </div>
        </div>
      </div>
      <div class="col-md-4 info">
        <p>ประเภททรัพย์สิน</p>
        <h6>{{ sourceLabel }}</h6>
        <p>บ้านเลขที่</p>
        <h6>{{ homePresenter.address_number || '-' }}</h6>
        <p>โครงการ</p>
        <h6>{{ homePresenter.project.name || '-' }}</h6>
        <p>ที่อยู่ (นอกระบบ)</p>
        <h6>{{ homePresenter.address_line || '-' }}</h6>
        <p>ประเภท</p>
        <h6>{{ homePresenter.type }}</h6>
        <p>พื้นที่ใช้สอย</p>
        <h6>{{ homePresenter.usable_area }} ตรม.</h6>
        <p>จำนวนชั้น</p>
        <h6>{{ homePresenter.floors }} ชั้น</h6>
        <p>จำนวนห้องนอน</p>
        <h6>{{ homePresenter.bedrooms }} ห้อง</h6>
        <p>จำนวนห้องน้ำ</p>
        <h6>{{ homePresenter.bathrooms }} ห้อง</h6>
        <p>ราคาฝากขาย</p>
        <h6>{{ homePresenter.sell_price | commas("0") }} บาท</h6>
        <p>ราคาปล่อยเช่า</p>
        <h6>{{ homePresenter.rent_price | commas("0") }} บาท</h6>
        <p>จุดประสงค์ของการฝากขายปล่อยเช่า</p>
        <h6 v-for="item in objectivePresenter" :key="item" class="objective">
          {{ item }}
        </h6>
        <p>ข้อมูลเพิ่มเติม</p>
        <h6>{{ notePresenter }}</h6>
      </div>
      <div class="col-md-4 info">
        <div class="update-status">
          <p>เปลี่ยนสถานะ</p>
          <sell-and-rent-status-select
            v-model="updatedStatus"
            :hide-item="handleHiddenItem"
            :show-cancel-item="canCancelForm"
            :cancelled-by-admin="isCancelledByAdmin"
            :disabled="isFormCancelled"
            class="status-select"
          ></sell-and-rent-status-select>
          <input
            v-model="feedbackRemark"
            :disabled="isFormCancelled"
            name="feedback-remark"
            type="text"
            class="form-control feedback-remark"
          />
          <div class="text-end">
            <sc-button
              :disabled="isFormCancelled || isSubmitting"
              class="status-button"
              variant="primary"
              @click="handleClickUpdateStatus"
              >บันทึก</sc-button
            >
          </div>
        </div>
        <hr />
        <div>
          <p>Remark ล่าสุด: {{ feedbackRemarkText }}</p>
          <h6>{{ updatedTime | date }}</h6>
        </div>
        <div v-for="status in statusPresenter" :key="status.label">
          <p>{{ status.label }}</p>
          <h6>{{ status.date | date }}</h6>
        </div>
      </div>
    </div>

    <!-- Image Modal -->
    <div v-if="showImageModal" class="image-modal" @click="closeImageModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <el-button
            class="download-button"
            icon="el-icon-download"
            @click="downloadImage"
          />
          <el-button
            class="close-button"
            icon="el-icon-close"
            @click="closeImageModal"
          />
        </div>
        <div class="modal-body">
          <img :src="selectedImage" class="modal-image" />
        </div>
        <div v-if="modalImages.length > 1" class="image-navigation">
          <el-button
            class="nav-button"
            icon="el-icon-arrow-left"
            @click="previousImage"
          />
          <span class="image-counter"
            >{{ selectedImageIndex + 1 }} / {{ modalImages.length }}</span
          >
          <el-button
            class="nav-button"
            icon="el-icon-arrow-right"
            @click="nextImage"
          />
        </div>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import { DateTime } from "luxon"
import Vue from "vue"
import { MessageBox } from "element-ui"

import { baanRueJaiClient } from "@/dependencies"
import LoadingOverlay from "@/lib/components/LoadingOverlay"
import PageWrapper from "@/lib/components/PageWrapper"
import ScButton from "@/lib/components/ScButton"
import { userRoleMapper } from "@/lib/mapper"

import SellAndRentStatusSelect from "../components/SellAndRentStatusSelect"
import {
  SellAndRentFilterOnlyStatuses,
  SellAndRentStatus,
  SellAndRentStatusEnum,
  SELL_AND_RENT_ADMIN_CANCEL,
  SELL_AND_RENT_CANCELLED_BY_ADMIN_LABEL,
} from "../SellAndRentStatus"
import { SellAndRentSource } from "../SellAndRentSource"
import {
  SellAndRentGrantorType,
  SellAndRentPowerOfAttorneyDocuments,
} from "../SellAndRentPowerOfAttorney"

export default Vue.extend({
  components: {
    LoadingOverlay,
    PageWrapper,
    ScButton,
    SellAndRentStatusSelect,
  },
  data() {
    const title = "รายละเอียดฝากขายปล่อยเช่า"
    const updatedStatus = null
    const updatedTime = null
    const feedbackRemark = null
    const feedbackRemarkText = null
    const form = null
    const isLoading = false
    const isSubmitting = false
    const showImageModal = false
    const selectedImage = null
    const selectedImageIndex = 0
    const modalImageList = null

    return {
      title,
      updatedStatus,
      updatedTime,
      feedbackRemark,
      feedbackRemarkText,
      form,
      isLoading,
      isSubmitting,
      showImageModal,
      selectedImage,
      selectedImageIndex,
      modalImageList,
    }
  },
  computed: {
    userPresenter() {
      const { user } = this.form

      return user
    },
    homePresenter() {
      const { home } = this.form

      return home
    },
    sourceLabel() {
      return SellAndRentSource[this.form.source] || "-";
    },
    objectivePresenter() {
      const { objectives } = this.form

      return objectives
    },
    notePresenter() {
      const { note } = this.form

      return note || "-"
    },
    userRole() {
      const { user_role: userRole } = this.form

      return userRoleMapper[userRole] || "-"
    },
    images() {
      const { images } = this.form

      if (!images) {
        return []
      }

      const imageUrls = images.map((image) => image.image_url)

      return imageUrls
    },
    powerOfAttorneyPresenter() {
      const { power_of_attorney: powerOfAttorney } = this.form

      if (!powerOfAttorney) {
        return null
      }

      const slots =
        SellAndRentPowerOfAttorneyDocuments[powerOfAttorney.grantor_type] || []
      const documents = slots.map(({ field, label }) => ({
        field,
        label,
        url: powerOfAttorney[field] || null,
      }))

      return { grantorType: powerOfAttorney.grantor_type, documents }
    },
    grantorTypeLabel() {
      return (
        SellAndRentGrantorType[this.powerOfAttorneyPresenter.grantorType] || "-"
      )
    },
    modalImages() {
      return this.modalImageList || this.images
    },
    statusPresenter() {
      const labels = {
        created_at: "อยู่ในระหว่างเจ้าหน้าที่ติดต่อกลับ",
        feedback_at: "ดำเนินการหาผู้ซื้อและ (หรือ) ผู้เช่า",
        approved_at: "ขาย หรือ ปล่อยเช่า เรียบร้อย",
        cancelled_at: "รายการถูกยกเลิก เนื่องจากลูกค้าแก้ไขข้อมูล",
        withdrawn_at: SellAndRentStatus.USER_CANCELLED,
      }

      return Object.keys(labels)
        .map((key) => {
          let label = labels[key]
          if (key === "cancelled_at" && this.isCancelledByAdmin) {
            label = SELL_AND_RENT_CANCELLED_BY_ADMIN_LABEL
          }
          if (
            label === "อยู่ในระหว่างเจ้าหน้าที่ติดต่อกลับ" &&
            this.form.is_resubmitted
          ) {
            label += " (ลูกค้าแก้ไขข้อมูล)"
          }

          if (label === SellAndRentStatus.USER_CANCELLED && this.form.withdrawn_reason) {
            label += ` เนื่องจาก ${this.form.withdrawn_reason}`
          }

          return { label, date: this.form[key] }
        })
        .filter(({ date }) => !!date)
    },
    currentStatus() {
      return this.form.status
    },
    statusWeight() {
      return Object.keys(SellAndRentStatus).filter(
        (status) =>
          status !== SellAndRentStatusEnum.CANCELLED &&
          status !== SellAndRentStatusEnum.USER_CANCELLED &&
          !SellAndRentFilterOnlyStatuses.includes(status),
      )
    },
    isFormCancelled() {
      const cancelledStatuses = [
        SellAndRentStatusEnum.CANCELLED,
        SellAndRentStatusEnum.USER_CANCELLED,
      ]

      return (
        this.currentStatus && cancelledStatuses.includes(this.currentStatus)
      )
    },
    isCancelledByAdmin() {
      return Boolean(this.form && this.form.cancelled_by_admin_id)
    },
    // Only a form that has not reached a terminal state (completed, cancelled,
    // withdrawn) can be cancelled by admin.
    canCancelForm() {
      return [
        SellAndRentStatusEnum.WAITING_FOR_AUTHORITY,
        SellAndRentStatusEnum.LOOKING_FOR_RENTERS,
      ].includes(this.currentStatus)
    },
  },
  created() {
    this.setupForm()
  },
  methods: {
    async setupForm() {
      this.isLoading = true
      try {
        const data = await this.fetchData()

        this.form = data.sell_and_rent_form
        this.updatedStatus = this.form.status
        this.feedbackRemarkText = this.form.feedback_remark
        this.updatedTime = this.form.updated_at
      } finally {
        this.isLoading = false
      }
    },
    async fetchData() {
      const response = await baanRueJaiClient.admin.homes.sellAndRent.getForm(
        this.$route.params.id,
      )

      return response.data.result
    },
    async handleClickUpdateStatus() {
      if (this.updatedStatus === SELL_AND_RENT_ADMIN_CANCEL) {
        await this.handleCancelForm()

        return
      }

      if (
        this.currentStatus === this.updatedStatus &&
        this.currentStatus === "COMPLETED"
      ) {
        return
      }

      const max = this.statusWeight.findIndex((s) => s === this.updatedStatus)
      const min = this.statusWeight.findIndex((s) => s === this.currentStatus)

      const map = {
        WAITING_FOR_AUTHORITY: "created_at",
        LOOKING_FOR_RENTERS: "feedback_at",
        COMPLETED: "approved_at",
      }

      const keys = this.statusWeight
        .filter((_, w) => w > min && w <= max)
        .map((key) => map[key])

      try {
        await MessageBox.confirm("ยืนยันการเปลี่ยนสถานะ", { confirmButtonText: "ตกลง", cancelButtonText: "ยกเลิก" })
      } catch {
        return
      }
      this.isSubmitting = true
      try {
        await this.updateStatus(keys)
        this.feedbackRemark = null
        await this.setupForm()
      } catch {
        this.$message.error("ไม่สามารถเปลี่ยนสถานะได้ กรุณาลองใหม่อีกครั้ง")
      } finally {
        this.isSubmitting = false
      }
    },
    async handleCancelForm() {
      try {
        await MessageBox.confirm("ยืนยันยกเลิกรายการฝากขายปล่อยเช่า", { confirmButtonText: "ตกลง", cancelButtonText: "ยกเลิก" })
      } catch {
        return
      }
      this.isSubmitting = true
      try {
        // Cancelling is a terminal branch, not a step in the status progression,
        // so it is sent directly instead of going through the status dropdown.
        await baanRueJaiClient.admin.homes.sellAndRent.put(
          this.$route.params.id,
          {
            cancelled_at: DateTime.utc().toISO(),
            feedback_remark: this.feedbackRemark,
          },
        )
        this.feedbackRemark = null
        await this.setupForm()
      } catch {
        this.$message.error("ไม่สามารถยกเลิกรายการได้ กรุณาลองใหม่อีกครั้ง")
      } finally {
        this.isSubmitting = false
      }
    },
    async updateStatus(statusList) {
      const { id } = this.$route.params
      const now = DateTime.utc().toISO()
      const params = statusList.reduce(
        (all, status) => ({
          ...all,
          [status]: now,
        }),
        {},
      )
      params.feedback_remark = this.feedbackRemark
      await baanRueJaiClient.admin.homes.sellAndRent.put(
        id,
        params,
      )
    },
    handleHiddenItem(status) {
      const current = this.statusWeight.findIndex(
        (s) => s === this.currentStatus,
      )
      const target = this.statusWeight.findIndex((s) => s === status)

      return target < current
    },
    openImageModal(image, index) {
      this.modalImageList = null
      this.selectedImage = image
      this.selectedImageIndex = index
      this.showImageModal = true
    },
    openPowerOfAttorneyModal(doc) {
      // Paging stays inside the power-of-attorney documents that were uploaded,
      // in the order they are listed, and never mixes in the detail images.
      const urls = this.powerOfAttorneyPresenter.documents
        .filter(({ url }) => !!url)
        .map(({ url }) => url)

      this.modalImageList = urls
      this.selectedImage = doc.url
      this.selectedImageIndex = urls.indexOf(doc.url)
      this.showImageModal = true
    },
    closeImageModal() {
      this.showImageModal = false
      this.selectedImage = null
      this.modalImageList = null
    },
    previousImage() {
      if (this.selectedImageIndex > 0) {
        this.selectedImageIndex -= 1
        this.selectedImage = this.modalImages[this.selectedImageIndex]
      } else {
        this.selectedImageIndex = this.modalImages.length - 1
        this.selectedImage = this.modalImages[this.selectedImageIndex]
      }
    },
    nextImage() {
      if (this.selectedImageIndex < this.modalImages.length - 1) {
        this.selectedImageIndex += 1
        this.selectedImage = this.modalImages[this.selectedImageIndex]
      } else {
        this.selectedImageIndex = 0
        this.selectedImage = this.modalImages[this.selectedImageIndex]
      }
    },
    async downloadImage() {
      try {
        const link = document.createElement("a")

        if (this.selectedImage.startsWith("http")) {
          const response = await fetch(this.selectedImage, { mode: "cors" })
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)

          link.href = url
          link.download = `${this.selectedImageIndex + 1}_${Date.now()}.jpg`

          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          window.URL.revokeObjectURL(url)
        } else {
          link.href = this.selectedImage
          link.download = `${this.selectedImageIndex + 1}_${Date.now()}.jpg`
          link.click()
        }
      } catch (error) {
        this.closeImageModal()

        this.$message.error(
          "ไม่สามารถดาวน์โหลดรูปภาพได้ กรุณาลองใหม่อีกครั้ง",
        )
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

$break-small: 600px;
$break-large: 1000px;

.wrapper {
  padding: $space 0;
}

.member h6,
.info h6 {
  margin-bottom: 20px;
}

.info {
  @media screen and (max-width: $break-small) {
    padding-top: 20px;
    border-top: $border;
  }

  @media screen and (min-width: $break-large) {
    border-left: $border;
  }

  > .objective {
    margin-bottom: 10px;
  }
}

.update-status {
  margin-bottom: 1rem;

  > .status-select {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  > .feedback-remark {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

.current-status {
  &.completed {
    color: $success-color;
  }

  &.rejected {
    color: $danger-color;
  }
}

.img-detail {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.power-of-attorney {
  margin-top: 20px;
  padding-top: 20px;
  border-top: $border;

  .section-title {
    font-weight: bold;
  }

  .poa-document {
    margin-bottom: 20px;
  }
}

.image-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 5px;
}

.image-container {
  position: relative;
}

.image-container:hover .img-detail {
  transform: scale(1.05);
}

.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  position: relative;
  width: 90vw;
  height: 80vh;
  background: white;
  border-radius: 8px;
  padding: 10px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.modal-header {
  position: absolute;
  border: none;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 10px;
  z-index: 10000;
}

.download-button {
  width: 40px;
  height: 40px;
  font-size: larger;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button {
  width: 40px;
  height: 40px;
  font-size: larger;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  height: 100%;
}

.image-navigation {
  display: flex;
  justify-content: space-between;
  justify-items: baseline;
  align-items: center;
  margin-top: 15px;
  padding: 0 20px;
}

.nav-button {
  border: none;
  width: 40px;
  height: 40px;
  font-size: larger;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-counter {
  font-size: 14px;
  color: #666;
}
</style>
